<template>
  <transition name="fade">
    <div v-if="readingIsReady" class="response-container-astrology">
      <!-- Close (X) Button in top right -->
      <button class="close-button-top-right" @click="exitReading" >
        <i class="fas fa-times"></i>
      </button>
      <!-- Main Layout Container -->
      <div class="content-flex-container">
      <!-- Teaser shown separately (can be conditionally displayed outside a lock screen) -->
      <div class="astrology-teaser" v-if="teaser">
        <blockquote class="teaser-quote">
          {{ teaser }}
        </blockquote>
      </div>
      <!-- 🔮 Styled Paywall Gate -->
      <div class="continue" v-if="ShowPayWall">
        <img
          v-if="image"
          :src="image"
          alt="Astrology Reading Image"
          class="reading-image-teaser"
        />
        <button class="pay_wall_button" @click="pay_wall_button" v-if="store.querent_id === ''">
          Login to Continue
        </button>

        <button
          class="pay_wall_button"
          @click="pay_wall_button"
          v-if="readingIsReady"
          :disabled="isLoading"
        >
          <span v-if="!isLoading">
            <!-- Priced by the server. A literal here read "25" while the
                 charge was 50 carats — half, and in the wrong unit. -->
            {{ formatAmount(astrologyCost) }}
            To See Your Personalized Astrology
          </span>
          <span v-else>
            <i class="fas fa-spinner fa-spin"></i>🔮 Personalizing Your Chart...
          </span>
        </button>


      </div>

<!-- Summary & Image Side-by-Side -->
<div class="reading-summary-wrapper" v-if="isReadingRevealed">
  <!-- Title section -->
  <div class="reading-summary-hero">
    <h1 class="reading-summary-title">{{ summaryTitle }}</h1>
  </div>

  <div class="primary-reading-container">
    <div class="primary-reading-box">
      <div class="card-position-label subtitle">{{ astrologySharedTitle }}</div>
      <div class="primary-reading" v-html="formatText(summary)"></div>
    </div>
  </div>

  <div class="primary-reading-image">
    <div class="card-position-label subtitle">{{ caption }}</div>
    <img
      v-if="image"
      :src="image"
      alt="Astrology Reading Image"
      class="reading-image-start"
      @click="expandImage"
    />
    <img
      v-else
      src="https://mysticaidablob.blob.core.windows.net/public/static/LoadingVideoGif.gif"
      alt="Loading"
      class="reading-image-loading"
    />
  </div>

  <!-- ⬇️ NEW: Separated Share + Finish section so we can move it on mobile -->
  <div class="reading-share-actions">
    <div class="center-share">
      <div class="share-and-exit-row">
<!--        <ShareContainer :sharing_state="is_shared"/>-->
        <button class="exit-button-beside-share" @click="exitReading">Finish Reading</button>
      </div>
    </div>
  </div>
</div>

      </div>

      <!-- Expanded image container -->
      <div
        v-if="isImageExpanded"
        class="expanded-image-container"
        @click="collapseImage"
      >
        <img
          :src="image"
          alt="Expanded Astrology Image"
          class="expanded-image"
        />
      </div>
    </div>
<div class="continue loading-container" v-else>
  <img
    :src="`https://mysticaidablob.blob.core.windows.net/public/static/LoadingVideoGif.gif?cacheBust=${gifKey}`"
    alt="Loading"
    class="reading-image-loading"
  />
  <p class="loading-headsup">{{ loadingHeadsUpMessage }}</p>
</div>

  </transition>
</template>

<script setup>
import { computed, ref } from 'vue';
import axios from "axios";
import { store, static_store } from '@/store';

import {
  astrologyTeaser,
  astrologySummary,
  astrologySummaryTitle, loadingHeadsUpMessage,
  astrologyImageCaption, readingIsReady,
  astrologyImage, astrologySharedTitle, resetAstrologyReadingState
} from '@/utils/reader-conversation';
import { readingPriceCarats, canAffordReading } from '@/utils/reading-price';

// import ShareContainer from "@/components/ui/ShareContainer.vue";

// What the reveal actually costs, from the active variant — the same number
// charge-for-reading will take. The button used to say a literal 25 while the
// charge was 100 carats.
const astrologyCost = computed(() => readingPriceCarats(store.readerSkillVariants));

const isReadingRevealed = computed(() =>
  store.latest_reading_session_is_paid || store.latest_reading_session_is_paid
);

const ShowPayWall = computed(() =>
  !store.querent_subscribed && !store.latest_reading_session_is_paid
);

const isLoading = ref(false);

async function pay_wall_button() {
  if (isLoading.value) return;
  isLoading.value = true;

  if (store.querent_id === '') {
    emit('update:showLogin', true);
    isLoading.value = false;
    return;
  }

  // Gate on the REAL price. `< 25` let a seeker through with too little and
  // failed them at the till instead.
  if (!canAffordReading({ balance: store.querent_carat_balance,
                          variants: store.readerSkillVariants,
                          subscribed: store.querent_subscribed })) {
    emit('showEmeraldShop');
    isLoading.value = false;
    return;
  }

  try {
    const response = await axios.get(`${static_store.apiUrl}/reading/charge-for-reading/`, {
      params: {
        reading_session_id: store.latest_reading_session,
        session_id: store.session_id,
      }
    });

    if (response.data.status === 'success') {
      store.latest_reading_session_is_paid = true;
      store.querent_carat_balance = response.data.carat_balance;
      store.querent_subscription_expiration = response.data.querent_subscription_expiration;
    } else {
      console.error('Charge failed:', response.data.error);
    }
  } catch (error) {
    if (error.response && error.response.status === 400) {
      emit('showEmeraldShop');
    } else {
      console.error('Network error:', error);
    }
  } finally {
    isLoading.value = false;
  }
}


// const is_shared = computed(() =>
//   store.is_latest_reading_session_shared === true || store.is_latest_reading_session_shared === 'true'
// );

const emit = defineEmits(['exitReading', 'showEmeraldShop']);
const teaser = computed(() => astrologyTeaser.value);
const summary = computed(() => astrologySummary.value);
const summaryTitle = computed(() => astrologySummaryTitle.value);
const caption = computed(() => astrologyImageCaption.value);
const image = computed(() => astrologyImage.value);
const isImageExpanded = ref(false);

function formatText(text) {
  return text.replace(/\n/g, '<br>');
}

function exitReading() {
  teaser.value = ''
  summary.value= ''
  summaryTitle.value= ''
  caption.value= ''
  image.value= ''
  readingIsReady.value = false

  resetAstrologyReadingState()
  readingIsReady.value = false

  emit('exitReading', store.current_reader_subdomain);
}

function expandImage() {
  isImageExpanded.value = true;
}

function collapseImage() {
  isImageExpanded.value = false;
}
</script>

<style scoped>
.response-container-astrology {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 100%;
  padding: 40px 10px 20px 10px;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #f9f9f9;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
}
.continue {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.reading-summary-hero {
  width: 98%;
  text-align: center;
  background-color: #f3e8ff;
  padding: 3px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
}

.reading-summary-title {
  font-size: 2em;
  font-weight: 700;
  color: #4b2666;
  margin: 10px 0;
  line-height: 1.2;
  text-align: center;
  font-family: 'Georgia', serif;
}

.content-flex-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.reading-summary-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  width: 100%;
}

/* These can remain as-is, or you can ensure consistent layout */
.primary-reading-container {
  flex: 1;
  min-width: 300px;
  max-width: 600px;
}

.primary-reading-box {
  background-color: #fff;
  padding: 10px;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.primary-reading {
  font-size: 1em;
  color: #333;
  line-height: 1.6;
}

.primary-reading-image {
  flex: 1;
  max-width: 320px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  padding: 10px;
}

.reading-image-start {
  width: 95%;
  height: auto;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  margin-top: 0;
}

.reading-image-teaser {
  width: 320px;
  height: auto;
  object-fit: cover;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  margin-top: 0;
}

.reading-share-actions {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.card-position-label.subtitle {
  font-size: 1.2em;
  color: #639;
  text-align: center;
  padding: 2px;
  border: 1px solid #639;
  background-color: #ffefd5;
  border-radius: 10px;
  margin-bottom: 10px;
}

.astrology-teaser {
  width: 100%;
  max-width: 800px;
  margin: 10px auto;
  text-align: center;
  padding: 5px 10px;
  background-color: #fff8fc;
  border-left: 5px solid #cc99ff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.teaser-quote {
  font-size: 1.5em;
  font-weight: 500;
  color: #663399;
  margin: 0;
  padding: 0;
  line-height: 1.6;
}

.close-button-top-right {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: white;
  color: black;
  border-color: #222222;
  border-radius: 50%;
  font-size: 16px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button-top-right:hover {
  background-color: #dddddd;
}

/* Expanded Image Overlay */
.expanded-image-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0.6);
}

.expanded-image {
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 10px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
  background-color: #fff;
}


.center-share {
  display: flex; /* Flexbox for centering */
  justify-content: center; /* Center content horizontally */
  align-items: center; /* Center content vertically */
  width: 100%; /* Full width of the parent container */
  margin: 5px 0; /* Add vertical spacing */
  text-align: center; /* Center text alignment if any */
}
.share-and-exit-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px; /* spacing between Share and Exit buttons */
  flex-wrap: nowrap;
}

.exit-button-beside-share {
  white-space: nowrap;
  background-color: #ff4d4d;
  color: white;
  font-weight: bold;
  border: none;
  padding: 12px 20px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 1em;
  max-width: 100%;
  transition: background-color 0.3s ease;
}

.exit-button-beside-share:hover {
  background-color: #cc0000;
}


/* This should be refactored/consolidated with the style from FourCardReading.vue*/
.pay_wall_button {
  background-color: #663399;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 1.1em;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 60px;
}

.pay_wall_button:hover {
  background-color: #7c51a8;
}

.pay_wall_button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}


.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px;
}
.reading-image-loading {
  max-width: 100%;
  height: auto;
}
.loading-headsup {
  margin-top: 16px;
  font-size: 1.3em;
  font-weight: 600;
  color: #ffffff;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.6);
  padding: 8px 16px;
  border-radius: 10px;
  max-width: 90%;
  line-height: 1.5;
}
@keyframes pulseFade {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.loading-headsup {
  animation: pulseFade 2s ease-in-out infinite;
}

@media (max-width: 768px) {
  .reading-summary-wrapper {
    flex-direction: column;
    align-items: center;
  }

  .primary-reading-container {
    order: 1;
  }

  .primary-reading-image {
    order: 2;
  }

  .reading-share-actions {
    order: 3;
  }
}
</style>
